/**
 * Route: `/terms`. Public Terms of Service page rendered inside PublicLayout.
 * Version 1.0 effective April 8, 2026.
 */
export function TermsPage() {
  return (
    <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 }}>
      <h2 style={{ marginBottom: 8 }}>terms of service v1.0 — effective april 8, 2026</h2>
      <p className="text-muted" style={{ marginBottom: 16 }}>
        AstdZ™ platform terms. please read before creating an account.
      </p>

      <hr className="divider" />

      <Section n="1" title="acceptance of terms">
        <p>
          by creating an account, requesting a certification, or using any part of the AstdZ™
          platform, you agree to these terms of service. we may update these terms from time
          to time. if you disagree with an update, you must stop using the platform and revoke
          your certifications.
        </p>
      </Section>

      <Section n="2" title="intellectual property">
        <p>
          the AstdZ™ name, logo, and brand are trademarks of AstdZ. the following standards
          are trademarks of AstdZ: EMRG™, POLI™, ENGY™, H2OQ™, TRAX™, AGRO™, STRX™, MEDI™,
          COLD™, ATMO™, WTHR™.
        </p>
        <p>
          the AstdZ Family Specification Preamble™ and the individual codec specifications
          are proprietary intellectual property of AstdZ. binary payload formats, byte
          layouts, field definitions, the tier architecture, message libraries, and reference
          tables are original works owned by AstdZ.
        </p>
        <p>
          the @astdz/codec reference implementation is licensed for use with the AstdZ
          platform only. users may not create derivative standards, fork specifications, or
          use the payload formats outside of the certified ecosystem without prior written
          permission from AstdZ. use of the standards requires a valid certification.
        </p>
      </Section>

      <Section n="3" title="certification terms">
        <p>
          certifications are granted at the sole discretion of AstdZ. test certifications are
          valid for 30 days, may be renewed once, and are limited to development and testing
          use. production certifications are valid for 60 days, are renewable, and may be
          used for live deployment.
        </p>
        <p>
          certifications may be revoked for violation of these terms. you must use the
          standards as specified and make no modifications to the payload format. you must
          maintain accurate device registrations. AstdZ reserves the right to audit
          implementations at any time.
        </p>
      </Section>

      <Section n="4" title="audit rights">
        <p>
          AstdZ may audit your use of the standards, including review of transmitted
          payloads, device configurations, firmware implementations, and integration code.
          you agree to cooperate with any audit within 30 days of a written request. failure
          to cooperate will result in revocation of your certification.
        </p>
      </Section>

      <Section n="5" title="acceptable use">
        <p>
          you must not transmit false emergency alerts. you must not interfere with legitimate
          emergency communications. you are responsible for the accuracy of every payload you
          transmit. misuse will result in immediate revocation and may be referred to the
          appropriate authorities.
        </p>
      </Section>

      <Section n="6" title="data and privacy">
        <p>
          AstdZ collects: organization name, email address, hashed passwords, device
          registrations, and payload metadata (timestamps, device ids, schema, and tier).
        </p>
        <p>
          AstdZ does not store raw payload content or free text after decoding. api usage is
          logged for rate limiting and abuse prevention. support chat conversations are
          processed by the Anthropic api and are subject to Anthropic's usage policies.
        </p>
      </Section>

      <Section n="7" title="disclaimer and limitation of liability">
        <p>
          the platform is provided "as is" during the beta period. AstdZ is not responsible
          for failed transmissions, radio interference, decoder errors, or actions taken or
          not taken on the basis of decoded alerts. maximum liability is limited to fees paid
          to AstdZ, which are currently $0 during the beta period.
        </p>
      </Section>

      <Section n="8" title="certification display rights">
        <p>
          organizations holding an active production certification may display the "AstdZ
          Certified™" mark. the mark must include the specific standard (for example,
          "EMRG™ Certified") and must link to the public verification url issued with the
          certification. the mark may only be displayed while the certification is active.
          specific language and badge formats will be provided upon issuance.
        </p>
      </Section>

      <Section n="9" title="governing law">
        <p>
          these terms are governed by the laws of the State of Indiana, United States. any
          disputes will be resolved in the courts of Hamilton County, Indiana.
        </p>
      </Section>

      <Section n="10" title="contact">
        <p>support@astdz.com</p>
        <p>AstdZ · Carmel, Indiana · United States</p>
      </Section>

      <Section n="11" title="beta program">
        <p>
          the AstdZ platform is currently in beta. features, pricing, and terms are subject
          to change. certifications are free during beta; pricing will be established before
          general availability. beta users will receive 90 days notice before any pricing
          changes take effect. beta data will be preserved into general availability. AstdZ
          reserves the right to end the beta program at any time.
        </p>
      </Section>

      <hr className="divider" />
      <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>
        AstdZ™ and EMRG™, POLI™, ENGY™, H2OQ™, TRAX™, AGRO™, STRX™, MEDI™, COLD™, ATMO™,
        WTHR™ are trademarks of AstdZ. all specifications and codec implementations are
        proprietary.
      </p>
    </div>
  );
}

function Section({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div className="text-muted" style={{ marginBottom: 6 }}>
        {`── ${n}. ${title} ${'─'.repeat(Math.max(0, 56 - title.length - n.length))}`}
      </div>
      <div>{children}</div>
    </div>
  );
}
